

"use client";

import { useCallback, useRef, useState } from "react";
import LorAuditPanel from "../../components/LorAuditPanel";
import ResumeSavePanel from "../../components/ResumeSavePanel"; // Changed import

const MAX_BYTES = 10 * 1024 * 1024; // 10MB
const ALLOWED_EXT = [".pdf", ".txt", ".docx"];
const ALLOWED_MIME = new Set([
  "application/pdf",
  "text/plain",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
]);

function cn(...cls) {
  return cls.filter(Boolean).join(" ");
}
function humanSize(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
function looksAllowed(file) {
  const name = (file?.name || "").toLowerCase();
  const type = (file?.type || "").toLowerCase();
  const extGood = ALLOWED_EXT.some((ext) => name.endsWith(ext));
  const mimeGood = ALLOWED_MIME.has(type);
  return extGood || mimeGood;
}

const Spinner = ({ className = "" }) => (
  <svg
    className={cn("animate-spin h-4 w-4 text-teal-600", className)}
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
      fill="none"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
    />
  </svg>
);

function SevBadge({ level }) {
  const map = {
    high: "bg-red-50 text-red-700 ring-red-200",
    medium: "bg-orange-50 text-orange-700 ring-orange-200",
    low: "bg-teal-50 text-teal-700 ring-teal-200",
  };
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset",
        map[level] || map.low
      )}
    >
      {level ? level[0].toUpperCase() + level.slice(1) : "Tip"}
    </span>
  );
}

export default function UploadAndAudit({
  onUseInBuilder, // optional: (text) => void
  meta = {},
  className = "",
}) {
  const [fileName, setFileName] = useState("");
  const [fileSize, setFileSize] = useState(0);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [hint, setHint] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [copied, setCopied] = useState(false);
  const [suggestions, setSuggestions] = useState([]); // AI tips
  const [sugLoading, setSugLoading] = useState(false);
  const [sugErr, setSugErr] = useState("");
  const [activeIdx, setActiveIdx] = useState(-1); // which bubble is open
  const [auditResults, setAuditResults] = useState(null); // Store audit results

  // Save panel modal state
  const [showSavePanel, setShowSavePanel] = useState(false);

  const inputRef = useRef(null);

  const showError = useCallback((message, more = "") => {
    setErr(message || "Failed to extract text");
    setHint(more || "");
  }, []);

  async function fetchSuggestions(theText) {
    if (!theText?.trim()) return;
    setSugErr("");
    setSugLoading(true);
    setActiveIdx(-1);
    try {
      const r = await fetch("/api/ai-suggestions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          text: theText,
          context: { docType: "resume", meta },
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data?.error || "Suggestion request failed");
      setSuggestions(Array.isArray(data?.suggestions) ? data.suggestions : []);
    } catch (e) {
      setSugErr(e?.message || "Could not get suggestions");
    } finally {
      setSugLoading(false);
    }
  }

  async function runExtract(file) {
    if (!file) return;
    setErr("");
    setHint("");
    setLoading(true);
    setFileName(file.name);
    setFileSize(file.size || 0);
    setText("");
    setSuggestions([]);
    setSugErr("");
    setAuditResults(null); // Reset audit results

    try {
      if (!looksAllowed(file)) {
        showError("Unsupported file type.", "Upload a PDF or TXT (DOCX optional).");
        return;
      }
      if (file.size > MAX_BYTES) {
        showError(
          `File too large (${humanSize(file.size)}).`,
          `Please upload a file ≤ ${humanSize(MAX_BYTES)}.`
        );
        return;
      }

      const form = new FormData();
      form.append("file", file);

      const r = await fetch("/api/extract-text", { method: "POST", body: form });
      const data = await r.json().catch(() => ({}));

      if (!r.ok) {
        const message = data?.error || "Failed to extract text";
        const extra =
          data?.hint ||
          (data?.code === "SCANNED_PDF_NO_TEXT"
            ? "This looks like a scanned PDF (images only). Run OCR (e.g., Adobe/Preview: 'Recognize Text') and re-upload."
            : "");
        showError(message, extra);
        return;
      }

      const extracted = data?.text || "";
      if (!extracted) {
        showError("No text was extracted from the file.");
        return;
      }
      setText(extracted);
      fetchSuggestions(extracted);
    } catch (e) {
      showError(e?.message || "Extraction failed");
    } finally {
      setLoading(false);
    }
  }

  async function handleFile(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    await runExtract(f);
  }

  // drag & drop
  const onDrop = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (!f) return;
    await runExtract(f);
  }, []);

  const onDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  }, []);

  const onDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  }, []);

  const onClickDrop = () => inputRef.current?.click();

  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(text || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch {}
  };

  // Handle loading saved analysis
  const handleLoadAnalysis = (savedData) => {
    if (savedData.resumeText) setText(savedData.resumeText);
    if (savedData.suggestions) setSuggestions(savedData.suggestions);
    if (savedData.auditResults) setAuditResults(savedData.auditResults);
    if (savedData.originalFileName) setFileName(savedData.originalFileName);
    setActiveIdx(-1);
    setShowSavePanel(false); // Close the modal
  };

  const charCount = text.length;

  return (
    <>
      <section
        aria-busy={loading ? "true" : "false"}
        className={cn(
          // all-white container & ring
          "border-gray-200 bg-white shadow-lg ring-1 ring-gray-200",
          "transition-colors",
          className
        )}
      >
        {/* header — solid white (no gradient) */}
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between border-b border-gray-200 px-5 py-5 md:px-8 bg-white">
          <div className="max-w-2xl">
            <h3 className="text-lg md:text-xl font-bold tracking-tight text-slate-900">
              Upload &amp; Audit Letter / Resume
            </h3>
            <p className="mt-1 text-[13px] md:text-sm text-gray-700">
              Drop in a <span className="font-medium">PDF</span> or{" "}
              <span className="font-medium">TXT</span> (DOCX optional), edit extracted
              text, then run the AI audit.
            </p>

            <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px] md:text-[12px]">
              <span className="rounded-full ring-1 ring-inset ring-gray-200 bg-gray-100 px-2 py-0.5 text-gray-700">
                Types: {ALLOWED_EXT.join(", ")}
              </span>
              <span className="rounded-full ring-1 ring-inset ring-gray-200 bg-gray-100 px-2 py-0.5 text-gray-700">
                Max {humanSize(MAX_BYTES)}
              </span>
              {fileName && (
                <span className="rounded-full ring-1 ring-inset ring-teal-200 bg-gray-100 px-2 py-0.5 text-gray-700">
                  Selected: {fileName} {fileSize ? `• ${humanSize(fileSize)}` : ""}
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 md:gap-3">
            <label className="cursor-pointer inline-flex items-center gap-2 rounded-xl bg-white px-3.5 py-2 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-200 hover:bg-slate-100 hover:text-teal-600">
              <input
                ref={inputRef}
                type="file"
                accept={ALLOWED_EXT.join(",")}
                className="hidden"
                onChange={handleFile}
              />
              <svg
                viewBox="0 0 24 24"
                className="h-4 w-4 text-gray-500"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden
              >
                <path d="M12 5v14m7-7H5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Choose file
            </label>

            <button
              type="button"
              onClick={() => {
                setFileName("");
                setFileSize(0);
                setText("");
                setErr("");
                setHint("");
                setSuggestions([]);
                setSugErr("");
                setActiveIdx(-1);
                setAuditResults(null);
                if (inputRef.current) inputRef.current.value = "";
              }}
              className="rounded-xl bg-white px-3.5 py-2 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-200 hover:bg-slate-100 hover:text-teal-600 active:scale-[.98]"
            >
              Clear
            </button>

            {/* NEW: Save Analysis button (opens modal with ResumeSavePanel) */}
            <button
              type="button"
              onClick={() => setShowSavePanel(true)}
              className="rounded-xl px-3.5 py-2 text-sm font-semibold text-white shadow-sm bg-gradient-to-r from-teal-600 to-orange-600 hover:from-teal-700 hover:to-orange-700 active:scale-[.98]"
              title="Save this analysis"
            >
              Save Analysis
            </button>
          </div>
        </div>

        {/* body */}
        <div className="px-5 md:px-8 pt-5">
          {/* Drop zone — default white; active uses teal→orange */}
          <div
            onDrop={onDrop}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onClick={onClickDrop}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onClickDrop()}
            className={cn(
              "grid place-items-center rounded-2xl border-2 border-dashed p-6 sm:p-8 md:p-10 transition focus:outline-none",
              dragOver
                ? "bg-gradient-to-r from-teal-500 to-orange-500 text-white border-transparent"
                : "border-gray-200 bg-white text-gray-700 hover:bg-slate-100",
              "focus:ring-2 focus:ring-teal-600"
            )}
            aria-label="Drag and drop a file here, or click to choose"
          >
            <div className="flex flex-col items-center text-center">
              <svg
                viewBox="0 0 24 24"
                className={cn("mb-2 h-9 w-9 sm:h-10 sm:w-10", dragOver ? "text-white" : "text-gray-500")}
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden
              >
                <path d="M12 16V4m0 0l-4 4m4-4l4 4M4 20h16" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <div className={cn("font-semibold text-sm sm:text-base", dragOver ? "text-white" : "text-slate-900")}>
                Drag &amp; drop your PDF/TXT/DOCX
              </div>
              <div className={cn("mt-1 text-[11px] sm:text-xs", dragOver ? "text-white/90" : "text-gray-500")}>
                Or click to browse • Max {humanSize(MAX_BYTES)}
              </div>
            </div>
          </div>

          {/* Status */}
          <div className="min-h-[1.25rem]">
            {loading && (
              <div className="mt-4 flex items-center gap-2 w-fit rounded-md ring-1 ring-inset ring-teal-600/30 bg-gray-100 px-3 py-2 text-sm text-teal-600">
                <Spinner />
                Extracting text…
              </div>
            )}
            {!!err && (
              <div className="mt-4 rounded-md ring-1 ring-inset ring-red-600/30 bg-gray-100 px-3 py-2 text-sm">
                <div className="font-semibold text-orange-700">Extraction error</div>
                <div className="text-gray-700">{err}</div>
                {hint ? <div className="mt-1 text-xs text-gray-700">{hint}</div> : null}
              </div>
            )}
          </div>

          {/* AI Suggestions (Info Bubbles) */}
          {(sugLoading || suggestions.length > 0 || sugErr) && (
            <div className="mt-6">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-slate-900">AI Suggestions</h4>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => fetchSuggestions(text)}
                    disabled={!text || sugLoading}
                    className={cn(
                      "inline-flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-sm font-semibold shadow-sm transition",
                      text && !sugLoading
                        ? "bg-white text-gray-700 ring-1 ring-inset ring-gray-200 hover:bg-slate-100 hover:text-teal-600"
                        : "bg-white/70 text-gray-500 ring-1 ring-inset ring-gray-200 cursor-not-allowed"
                    )}
                  >
                    {sugLoading ? (
                      <Spinner />
                    ) : (
                      <svg
                        viewBox="0 0 24 24"
                        className="h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        aria-hidden
                      >
                        <path d="M4 4v6h6M20 20v-6h-6M20 8l-6 6M4 16l6-6" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                    {sugLoading ? "Generating…" : "Refresh"}
                  </button>
                </div>
              </div>

              {sugErr && (
                <div className="mt-3 rounded-md ring-1 ring-inset ring-red-600/30 bg-gray-100 px-3 py-2 text-sm text-orange-700">
                  {sugErr}
                </div>
              )}

              {/* Bubble strip */}
              <div className="mt-3 flex flex-wrap gap-2">
                {sugLoading &&
                  Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="h-9 w-40 animate-pulse rounded-full bg-gray-200" />
                  ))}

                {!sugLoading &&
                  suggestions.map((sug, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setActiveIdx((prev) => (prev === i ? -1 : i))}
                      className={cn(
                        "group inline-flex items-center gap-2 rounded-full px-3 py-2 text-sm shadow-sm ring-1 ring-inset transition",
                        "bg-white ring-gray-200 hover:bg-slate-100",
                        activeIdx === i && "ring-teal-600/50"
                      )}
                      title={sug?.summary || sug?.title}
                    >
                      {/* lightbulb icon */}
                      <svg
                        viewBox="0 0 24 24"
                        className="h-4 w-4 text-teal-600"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        aria-hidden
                      >
                        <path
                          d="M9 18h6M10 22h4M12 2a7 7 0 00-4 12c.5.5 1 1.5 1 2h6c0-.5.5-1.5 1-2A7 7 0 0012 2z"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <span className="font-medium text-gray-700">{sug?.title || "Suggestion"}</span>
                      <SevBadge level={sug?.severity} />
                    </button>
                  ))}
              </div>

              {/* Expanded panel */}
              {activeIdx >= 0 && suggestions[activeIdx] && (
                <div className="mt-4 rounded-2xl border border-gray-200 bg-white p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <h5 className="text-sm font-semibold text-slate-900">
                          {suggestions[activeIdx].title}
                        </h5>
                        <SevBadge level={suggestions[activeIdx].severity} />
                      </div>
                      {suggestions[activeIdx].summary && (
                        <p className="mt-1 text-sm text-gray-700">{suggestions[activeIdx].summary}</p>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => setActiveIdx(-1)}
                      className="rounded-lg px-2 py-1 text-xs font-semibold text-gray-700 hover:bg-slate-100 hover:text-teal-600"
                    >
                      Close
                    </button>
                  </div>

                  {(suggestions[activeIdx].rationale ||
                    suggestions[activeIdx].example_before ||
                    suggestions[activeIdx].example_after) && (
                    <div className="mt-3 grid gap-3 md:grid-cols-2">
                      {suggestions[activeIdx].rationale && (
                        <div className="rounded-xl bg-gray-100 p-3 ring-1 ring-inset ring-gray-200">
                          <div className="text-xs font-semibold text-gray-700 mb-1">Why it matters</div>
                          <p className="text-sm text-gray-700">{suggestions[activeIdx].rationale}</p>
                        </div>
                      )}
                      {(suggestions[activeIdx].example_before ||
                        suggestions[activeIdx].example_after) && (
                        <div className="rounded-xl bg-gray-100 p-3 ring-1 ring-inset ring-gray-200">
                          <div className="text-xs font-semibold text-gray-700 mb-1">Example</div>
                          {suggestions[activeIdx].example_before && (
                            <pre className="text-xs whitespace-pre-wrap text-gray-700">
                              <span className="font-semibold">Before: </span>
                              {suggestions[activeIdx].example_before}
                            </pre>
                          )}
                          {suggestions[activeIdx].example_after && (
                            <pre className="text-xs whitespace-pre-wrap text-gray-700 mt-2">
                              <span className="font-semibold">After: </span>
                              {suggestions[activeIdx].example_after}
                            </pre>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="mt-3 flex items-center gap-2">
                    <button
                      type="button"
                      onClick={async () => {
                        const tip =
                          suggestions[activeIdx].example_after ||
                          suggestions[activeIdx].summary ||
                          suggestions[activeIdx].title ||
                          "";
                        if (!tip) return;
                        try {
                          await navigator.clipboard.writeText(tip);
                        } catch {}
                      }}
                      className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold shadow-sm bg-white text-gray-700 ring-1 ring-inset ring-gray-200 hover:bg-slate-100 hover:text-teal-600"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        className="h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        aria-hidden
                      >
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                        <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                      </svg>
                      Copy tip
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Responsive two-column: Editor | Audit Panel */}
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Editor */}
            <div className="order-1">
              <label className="block text-[12.5px] sm:text-[13px] font-semibold text-slate-900">
                Extracted text <span className="text-gray-500 font-normal">(editable)</span>
              </label>

              <div className="mt-2 rounded-2xl border border-gray-200 bg-white shadow-sm focus-within:ring-2 focus-within:ring-teal-600">
                <textarea
                  rows={12}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Upload a file, or paste text here…"
                  className="w-full resize-y rounded-2xl bg-transparent px-3.5 py-3 font-[440] text-slate-900 outline-none placeholder:text-gray-500 min-h-[280px] sm:min-h-[340px] lg:min-h-[420px]"
                  style={{ fontFamily: '"UI Serif", "Times New Roman", Times, serif' }}
                />

                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 border-t border-gray-200 px-3.5 py-2.5 text-xs">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      disabled={!text}
                      onClick={() => onUseInBuilder && onUseInBuilder(text)}
                      className={cn(
                        "inline-flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-sm font-semibold shadow-sm transition active:scale-[.98]",
                        text
                          ? "bg-gradient-to-r from-teal-600 to-orange-600 text-white hover:from-teal-700 hover:to-orange-700"
                          : "bg-white/70 text-gray-500 ring-1 ring-inset ring-gray-200 cursor-not-allowed"
                      )}
                    >
                      <svg
                        viewBox="0 0 24 24"
                        className={cn("h-4 w-4", text ? "text-white" : "text-gray-500")}
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        aria-hidden
                      >
                        <path d="M8 17l4 4 4-4M12 12v9M20 7H4" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      Use in Builder
                    </button>

                    <button
                      type="button"
                      onClick={doCopy}
                      disabled={!text}
                      className={cn(
                        "inline-flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-sm font-semibold shadow-sm transition active:scale-[.98]",
                        text
                          ? "bg-white text-gray-700 ring-1 ring-inset ring-gray-200 hover:bg-slate-100 hover:text-teal-600"
                          : "bg-white/70 text-gray-500 ring-1 ring-inset ring-gray-200 cursor-not-allowed"
                      )}
                    >
                      <svg
                        viewBox="0 0 24 24"
                        className="h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        aria-hidden
                      >
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                        <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                      </svg>
                      {copied ? "Copied!" : "Copy"}
                    </button>
                  </div>

                  <div className="text-gray-500">{charCount.toLocaleString()} chars</div>
                </div>
              </div>
            </div>

            {/* Audit Panel */}
            <div className="order-2">
              <LorAuditPanel
                text={text}
                meta={meta}
                onApplyRewrite={(newText) => setText(newText)}
                onAuditComplete={(results) => setAuditResults(results)}
                className="mt-0 lg:mt-6 rounded-2xl border border-gray-200 bg-white shadow-sm"
              />
            </div>
          </div>
        </div>

        <div className="pb-5 px-5 md:px-8" />
      </section>

      {/* Modal: ResumeSavePanel */}
      {showSavePanel && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          role="dialog"
          aria-modal="true"
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setShowSavePanel(false)}
            aria-hidden="true"
          />
          {/* Dialog */}
          <div className="relative z-10 w-[95vw] max-w-4xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white ring-1 ring-gray-200 shadow-xl p-4 sm:p-6">
            <div className="flex items-start justify-between gap-3">
              <h4 className="text-base sm:text-lg font-semibold text-slate-900">
                Save Resume Analysis
              </h4>
              <button
                type="button"
                onClick={() => setShowSavePanel(false)}
                className="rounded-lg px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-slate-100 hover:text-teal-600"
                aria-label="Close"
              >
                Close
              </button>
            </div>

            <div className="mt-4">
              <ResumeSavePanel
                resumeText={text}
                suggestions={suggestions}
                auditResults={auditResults}
                originalFileName={fileName}
                defaultName="Resume_Analysis"
                storageKey="resume-analysis"
                onLoad={handleLoadAnalysis}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}